import * as React from 'react';
import './pdf.css';
import { observer, inject } from 'mobx-react';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { FurtherConsiderationsEntryStore } from '../stores/further-considerations-entry-store';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';

import { Bloodwork } from '../models/Bloodwork';
import { Button, Card, Image } from 'react-bootstrap';

import { BloodworkAnalysis } from '../models/BloodworkAnalysis';
import { PanelField } from '../models/PanelField';
import Moment from 'react-moment';
import moment from 'moment';
import { FurtherConsiderations, PanelMarker, Section } from '../models';
import { DisclaimerSection } from '../components/bloodworkz/disclaimer-section';
import { PDFSection } from '../components/bloodworkz/pdf-section';
import { SummaryDashboardSection } from '../components/bloodworkz/summary-dashboard-section';
import Storage from '@aws-amplify/storage';

const logo = require('../assets/imgs/logo.png');
const html2pdf = require('html2pdf.js');

Storage.configure({
  AWSS3: {
    bucket: 'labsmarts-next',
    region: 'us-east-1',
  },
});

interface BloodworkAnalysisPDFContainerProps {
  bloodworkEntryStore?: BloodworkEntryStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
  userProfileUIStore?: UserProfileUIStore;
}

type State = {
  withNotes: boolean;
  bloodworkAnalysis: BloodworkAnalysis;
  previousBloodwork?: Bloodwork;
  furtherConsiderations: FurtherConsiderations;
  headerLogoDataUrl: string | null;
  footerBrandDataUrl: string | null;
  isPdfReady: boolean;
  preparedPdf: any | null;
  collapsedSections: string[];
};

@inject(
  'bloodworkEntryStore',
  'furtherConsiderationsEntryStore',
  'userProfileUIStore',
)
@observer
export class BloodworkAnalysisPDFContainer extends React.Component<
  BloodworkAnalysisPDFContainerProps,
  State
> {
  state: State = {
    withNotes: false,
    bloodworkAnalysis: new BloodworkAnalysis(),
    previousBloodwork: undefined,
    furtherConsiderations: new FurtherConsiderations(),
    headerLogoDataUrl: null,
    footerBrandDataUrl: null,
    isPdfReady: false,
    preparedPdf: null,
    collapsedSections: [],
  };

  // --- Helpers ---------------------------------------------------------------

  // Turn any URL into a data URL (so html2canvas can render it reliably)
  private toDataUrl = async (url: string): Promise<string> => {
    const res = await fetch(url, { credentials: 'omit' });
    const blob = await res.blob();
    return await new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  // Resolve the HEADER logo (custom if present; otherwise bundled default) to a data URL
  private resolveHeaderLogoDataUrl = async (): Promise<string> => {
    const raw = this.getLogoUrl() as any;
    const candidate = (raw?.default ?? raw) as string | undefined;

    if (candidate === logo) {
      return await this.toDataUrl(candidate);
    }

    // 1) If getLogoUrl() returns http(s), try directly
    if (candidate && /^https?:\/\//i.test(candidate)) {
      try {
        return await this.toDataUrl(candidate);
      } catch {
        /* fall through */
      }
    }

    // 2) Try S3: user-logos/<filename>
    try {
      const key = (candidate || '').toString().split('/').pop();
      if (key) {
        const s3Url = await Storage.get(`user-logos/${key}`);
        return await this.toDataUrl(s3Url as string);
      }
    } catch {
      /* fall through */
    }

    // 3) Fallback: bundled default
    const bundled =
      (require('../assets/imgs/logo.png') as any)?.default ||
      (require('../assets/imgs/logo.png') as any);
    return await this.toDataUrl(bundled);
  };

  // Resolve the FOOTER brand mark (usually the bundled logo) to a data URL
  private resolveFooterBrandDataUrl = async (): Promise<string> => {
    const brandUrl =
      (require('../assets/imgs/logo.png') as any)?.default ||
      (require('../assets/imgs/logo.png') as any);
    return await this.toDataUrl(brandUrl);
  };

  // Prepare logos and PDF data in advance
  private preparePdfAssets = async (): Promise<void> => {
    try {
      // Load logos in parallel
      const [headerLogoDataUrl, footerBrandDataUrl] = await Promise.all([
        this.resolveHeaderLogoDataUrl(),
        this.resolveFooterBrandDataUrl(),
      ]);

      this.setState({
        headerLogoDataUrl,
        footerBrandDataUrl,
        isPdfReady: true,
      });

      // Update the logo in DOM immediately for better UX
      const logoElement = document.getElementById('logo') as HTMLImageElement;
      if (logoElement && headerLogoDataUrl) {
        logoElement.src = headerLogoDataUrl;
      }

      // Now prepare the actual PDF
      await this.preparePdf();
    } catch (error) {
      console.error('Error preparing PDF assets:', error);
      // Set ready to true anyway so export can still work with fallback
      this.setState({ isPdfReady: true });
      this.showOrHidePdfExportButton(true);
    }
  };

  // Prepare the actual PDF object in advance
  private preparePdf = async (): Promise<void> => {
    try {
      const { bloodworkAnalysis, headerLogoDataUrl, footerBrandDataUrl } =
        this.state;

      if (!bloodworkAnalysis.bloodwork) {
        console.error('Bloodwork analysis not ready');
        return;
      }

      const firstInitialOfFirstName =
        bloodworkAnalysis.bloodwork.client.firstName.charAt(0);
      const lastName = bloodworkAnalysis.bloodwork.client.lastName;
      const collectionDate = moment(
        bloodworkAnalysis.bloodwork.collectionDate,
      ).format('YYYY-MM-DD');
      const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Analysis_${collectionDate}.pdf`;

      const rootEl = document.getElementById('test-results-analysis-panel');
      const pdfExportButton = document.getElementById('pdf-export-button');
      const cardHeaders = document.getElementsByClassName('card-header');

      if (pdfExportButton) pdfExportButton.style.opacity = '0';

      if (cardHeaders) {
        for (let i = 0; i < cardHeaders.length; i++) {
          (cardHeaders[i] as HTMLElement).style.marginTop = '-1px';
        }
      }

      if (!rootEl) {
        console.error('PDF root element not found');
        return;
      }

      // Helper: detect 'PNG' or 'JPEG' from data URL
      const detectType = (dataUrl: string): 'PNG' | 'JPEG' => {
        return dataUrl.startsWith('data:image/jpeg') ||
          dataUrl.startsWith('data:image/jpg')
          ? 'JPEG'
          : 'PNG';
      };
      const footerImgType = footerBrandDataUrl
        ? detectType(footerBrandDataUrl)
        : 'PNG';

      const opt = {
        margin: [5, -2, 5, 0],
        filename,
        image: { type: 'jpeg', quality: 1 },
        jsPDF: { orientation: 'p' },
        pagebreak: { mode: ['css', 'legacy'], avoid: ['.disclaimer-panel', '.pdf-section-with-notes'] },
        html2canvas: {
          scale: 1,
          useCORS: true,
          allowTaint: false,
          scrollX: 0,
          scrollY: 0,
          onclone: (doc: Document) => {
            const img = doc.getElementById('logo') as HTMLImageElement | null;
            if (img && headerLogoDataUrl) img.src = headerLogoDataUrl;
          },
        },
      };

      // Generate PDF and add footer
      const preparedPdf = await html2pdf()
        .set(opt)
        .from(rootEl)
        .toPdf()
        .get('pdf')
        .then((pdf: any) => {
          // Only add footer if we have the brand data URL
          if (footerBrandDataUrl) {
            // Compute brand logo aspect ratio from the data URL
            const props = pdf.getImageProperties(footerBrandDataUrl);
            const naturalRatio = props ? props.width / props.height : 5.5; // fallback ratio

            const footerFontSize = 8;
            const logoHeight = 3.8; // tweak to taste
            const logoWidth = logoHeight * naturalRatio;

            const rightText = 'Powered by';
            const leftPadding = 8;
            const rightPadding = 8;
            const bottomPadding = 6;
            const gap = 2;

            const fullName = `${bloodworkAnalysis.bloodwork.client.firstName} ${bloodworkAnalysis.bloodwork.client.lastName}`;
            const formattedDate = moment(
              bloodworkAnalysis.bloodwork.collectionDate,
            ).format('MM/DD/YYYY');

            // Add Prepared By if available
            const { userProfileUIStore } = this.props;
            const preparedBy =
              userProfileUIStore?.currentUserProfile?.preparedBy;

            let leftBase = `Prepared for: ${fullName}  •  Collection Date: ${formattedDate}`;
            if (preparedBy) {
              leftBase += `  •  Prepared by: ${preparedBy}`;
            }

            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();
              const baselineY = pageHeight - bottomPadding;

              pdf.setFontSize(footerFontSize);

              // Right block width
              const rightTextWidth = pdf.getTextWidth(rightText);
              const rightBlockWidth = rightTextWidth + gap + logoWidth;
              const rightStartX = pageWidth - rightPadding - rightBlockWidth;

              // Fit left text to avoid overlap
              const maxLeftWidth = rightStartX - leftPadding - 4;
              const fit = (s: string) => {
                if (pdf.getTextWidth(s) <= maxLeftWidth) return s;
                let t = s;
                while (t.length > 4 && pdf.getTextWidth(t + '…') > maxLeftWidth)
                  t = t.slice(0, -1);
                return t + '…';
              };
              const leftText = fit(leftBase);

              // Draw left and right blocks
              pdf.text(leftText, leftPadding, baselineY);
              pdf.text(rightText, rightStartX, baselineY);
              pdf.addImage(
                footerBrandDataUrl,
                footerImgType,
                rightStartX + rightTextWidth + gap,
                baselineY - logoHeight + 0.8,
                logoWidth,
                logoHeight,
              );
            }
          }

          return pdf;
        });

      this.setState({
        preparedPdf,
        isPdfReady: true,
      });

      // Show button only when PDF is ready
      this.showOrHidePdfExportButton(true);
      if (pdfExportButton) pdfExportButton.style.opacity = '1';
    } catch (error) {
      console.error('Error preparing PDF:', error);
      // Set ready to true anyway so export can still work with fallback
      this.setState({ isPdfReady: true });
      this.showOrHidePdfExportButton(true);
    }
  };

  // --------------------------------------------------------------------------

  componentDidMount() {
    this.showOrHidePdfExportButton(false);

    const withNotes = sessionStorage.getItem('exportWithNotes') === 'true';
    const bloodworkJSON = sessionStorage.getItem('bloodwork');
    const previousBloodworkJSON = sessionStorage.getItem('previousBloodwork');
    const collapsedSectionsJSON = sessionStorage.getItem('collapsedSections');
    const collapsedSections: string[] = collapsedSectionsJSON
      ? JSON.parse(collapsedSectionsJSON)
      : [];
    sessionStorage.clear();

    // Ensure profile is loaded (for custom logo)
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore && !userProfileUIStore.currentUserProfile) {
      userProfileUIStore.loadCurrentUserProfile();
    }

    this.props.bloodworkEntryStore
      ?.loadBloodworkRanges()
      .then(bloodworkRanges => {
        const bloodwork = new Bloodwork(JSON.parse(bloodworkJSON!));
        const previousBloodwork = previousBloodworkJSON
          ? new Bloodwork(JSON.parse(previousBloodworkJSON))
          : undefined;

        const bloodworkAnalysis = new BloodworkAnalysis({
          bloodwork,
          bloodworkRanges,
          bloodworkFields: PanelField.ALL_FIELDS,
        });
        bloodworkAnalysis.init().performAnalysis();

        if (previousBloodwork && previousBloodwork.collectionDate) {
          const previousBloodworkAnalysis = new BloodworkAnalysis({
            bloodwork: previousBloodwork,
            bloodworkRanges,
            bloodworkFields: PanelField.ALL_FIELDS,
          });
          previousBloodworkAnalysis.init();
          bloodworkAnalysis.preparePreviousPanelMarkers(
            previousBloodworkAnalysis,
          );
        }
        return { bloodworkAnalysis, previousBloodwork };
      })
      .then(({ bloodworkAnalysis, previousBloodwork }) => {
        return this.props
          .furtherConsiderationsEntryStore!.loadFurtherConsiderations()
          .then(furtherConsiderations => ({
            bloodworkAnalysis,
            previousBloodwork,
            furtherConsiderations,
          }));
      })
      .then(
        ({ bloodworkAnalysis, previousBloodwork, furtherConsiderations }) => {
          this.setState({
            withNotes,
            bloodworkAnalysis,
            previousBloodwork,
            furtherConsiderations,
            collapsedSections,
          });
        },
      );
  }

  componentDidUpdate(_prevProps: any, prevState: State) {
    if (
      !Object.values(prevState.bloodworkAnalysis).filter(Boolean).length &&
      Object.values(this.state.bloodworkAnalysis).filter(Boolean).length
    ) {
      setTimeout(() => this.preparePdfAssets(), 2000);
    }
  }

  componentWillUnmount() {
    sessionStorage.clear();
  }

  componentWillReceiveProps() {
    this.scrollTop();
  }

  scrollTop() {
    if (
      typeof window !== 'undefined' &&
      typeof window.scrollTo === 'function'
    ) {
      window.scrollTo(0, 0);
    }
  }

  showOrHidePdfExportButton(show: boolean, timeout: number = 0) {
    setTimeout(() => {
      const opacity = show ? '1' : '0';
      const pdfExportButton = document.getElementById('pdf-export-button');
      if (pdfExportButton) {
        pdfExportButton.style.opacity = opacity;
      }
    }, timeout);
  }

  getLogoUrl = (): string => {
    const { userProfileUIStore } = this.props;
    const userProfile = userProfileUIStore?.currentUserProfile;
    return (
      userProfile?.hasCustomLogo() ? userProfile.customLogoUrl : logo
    ) as any;
  };

  // Legacy helper still used elsewhere
  getImageAsDataUrl = async (url: string) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  // ========= EXPORT =========
  export = async () => {
    const { preparedPdf, isPdfReady, bloodworkAnalysis } = this.state;
    const firstInitialOfFirstName =
      bloodworkAnalysis.bloodwork.client.firstName.charAt(0);
    const lastName = bloodworkAnalysis.bloodwork.client.lastName;
    const collectionDate = moment(
      bloodworkAnalysis.bloodwork.collectionDate,
    ).format('YYYY-MM-DD');
    const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Analysis_${collectionDate}.pdf`;

    if (preparedPdf && isPdfReady) {
      preparedPdf.save(filename);
    } else {
      // Fallback: prepare PDF on the fly (shouldn't happen normally)
      console.log('PDF not prepared, falling back to on-demand generation...');
      await this.preparePdf();
      if (this.state.preparedPdf) {
        this.state.preparedPdf.save(filename);
      }
    }
  };

  pageBreakClassName(panelMarkers: PanelMarker[]): string {
    return panelMarkers.length > 0 ? 'html2pdf__page-break' : '';
  }

  render() {
    const { bloodworkAnalysis, furtherConsiderations, isPdfReady, collapsedSections } = this.state;
    if (!bloodworkAnalysis.bloodwork) {
      return null;
    }
    const isCollapsed = (section: Section) => collapsedSections.includes(section);
    const today = new Date().toISOString();

    // Only show Immune System Analysis if WBC has a value
    const wbc = (bloodworkAnalysis as any).wbc;
    const hasWbcValue = wbc?.hasValue?.() === true;

    // Clean leading blank lines/spaces from the global Notes block
    const notesObj = bloodworkAnalysis.bloodwork.bloodworkNotes.notes.find(
      n => n.section === Section.NOTES,
    );
    const globalNotesText = (notesObj?.text ?? '').trimStart();

    // Split Hormones section if there are more than 14 markers
    const sexHormoneMarkers = bloodworkAnalysis.getSexHormonePanelMarkers();
    const hormonesFirstPageMarkers = sexHormoneMarkers.slice(0, 13);
    const hormonesSecondPageMarkers = sexHormoneMarkers.slice(13);

    // Split Nutrients section if there are more than 14 markers
    const mineralMarkers = bloodworkAnalysis.getMineralPanelMarkers();
    const nutrientsFirstPageMarkers = mineralMarkers.slice(0, 13);
    const nutrientsSecondPageMarkers = mineralMarkers.slice(13);

    // Collect ALL markers from every section for the summary views.
    // We use a Set to deduplicate by object identity (same PanelMarker instance
    // may appear in multiple sections, e.g. globulin appears in WBC and Proteins).
    const allMarkerSet = new Set<PanelMarker>([
      ...bloodworkAnalysis.getRBCPanelMarkers(),
      ...bloodworkAnalysis.getIronPanelMarkers(),
      ...bloodworkAnalysis.getWBCPanelMarkers(),
      ...bloodworkAnalysis.getPlateletPanelMarkers(),
      ...bloodworkAnalysis.getGlucosePanelMarkers(),
      ...bloodworkAnalysis.getKidneyPanelMarkers(),
      ...bloodworkAnalysis.getProteinsPanelMarkers(),
      ...bloodworkAnalysis.getMineralPanelMarkers(),
      ...bloodworkAnalysis.getElectrolytePanelMarkers(),
      ...bloodworkAnalysis.getLiverPanelMarkers(),
      ...bloodworkAnalysis.getLipidsPanelMarkers(),
      ...bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers(),
      ...bloodworkAnalysis.getThyroidPanelMarkers(),
      ...bloodworkAnalysis.getSexHormonePanelMarkers(),
      ...bloodworkAnalysis.getProstatePanelMarkers(),
    ]);
    const allMarkers = Array.from(allMarkerSet);

    // "Higher is better" markers (eGFR, PSA Free, PSA % Free) have no upper alarm bound
    // and should never appear in Above Optimal.
    const isHigherIsBetter = (pm: PanelMarker) =>
      pm.isEGFR?.() || pm.field?.useEGFRChart;

    // RDW-chart markers render everything below optimalMax as green — low values are fine,
    // so they must never appear in Below Optimal.
    const isRdwChart = (pm: PanelMarker) => pm.field?.useRDWChart;

    // For "higher is better" markers, alarmMin/labMin can be 0 (clamped from a negative
    // computed value), making isLessThanAlarm/isLessThanLab unreliable. Compare directly
    // against the stored range values and only use a threshold when it is > 0.
    const egfrBelowTier = (pm: PanelMarker): 1 | 2 | 3 => {
      const r = pm.range;
      if (!r) return 3;
      if (r.alarmMin > 0 && pm.value < r.alarmMin) return 1;
      if (r.labMin > 0 && pm.value < r.labMin) return 2;
      return 3;
    };

    // Zone-penetration score: fraction of the relevant danger zone consumed.
    // WBC uses its own 9-zone percentFromMean which is already zone-normalized.
    // Falls back to |percentFromMean| when denominators collapse to zero.
    const zonePenetrationScore = (pm: PanelMarker, section: 'alarm' | 'lab' | 'optimal'): number => {
      const r = pm.range;
      if (!r) return Math.abs(pm.percentFromMean ?? 0);
      if (pm.field?.useWBCPercentFromMean) return Math.abs(pm.percentFromMean ?? 0);
      const v = pm.value;
      if (section === 'alarm') {
        const denom = r.stdDev && r.stdDev > 0 ? r.stdDev : 1;
        if (v > r.alarmMax) return (v - r.alarmMax) / denom;
        if (r.alarmMin > 0 && v < r.alarmMin) return (r.alarmMin - v) / denom;
        return 0;
      }
      if (section === 'lab') {
        if (v > r.labMax) {
          const denom = r.alarmMax - r.labMax;
          return denom > 0 ? (v - r.labMax) / denom : Math.abs(pm.percentFromMean ?? 0);
        }
        const denom = r.labMin - (r.alarmMin ?? 0);
        return denom > 0 ? (r.labMin - v) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      // section === 'optimal'
      if (v > (r.optimalMax ?? r.labMax)) {
        const denom = r.labMax - (r.optimalMax ?? r.labMax);
        return denom > 0 ? (v - r.optimalMax!) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      const optMin = r.optimalMin ?? r.labMin;
      const denomOpt = optMin - r.labMin;
      return denomOpt > 0 ? (optMin - v) / denomOpt : Math.abs(pm.percentFromMean ?? 0);
    };

    const alarmZoneDesc   = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'alarm')   - zonePenetrationScore(a, 'alarm');
    const labZoneDesc     = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'lab')     - zonePenetrationScore(a, 'lab');
    const optimalZoneDesc = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'optimal') - zonePenetrationScore(a, 'optimal');

    // "Alarm Range Markers" — outside alarm threshold in either direction, worst first
    const needsAttentionGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 1
          : (pm.isGreaterThanAlarm?.() || (!isRdwChart(pm) && pm.isLessThanAlarm?.()))
      ))
      .sort(alarmZoneDesc);

    // "Outside Lab Range Markers" — outside lab range but within alarm, worst first
    const worthDiscussingGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 2
          : (
              (pm.isGreaterThanLab?.() && !pm.isGreaterThanAlarm?.()) ||
              (!isRdwChart(pm) && pm.isLessThanLab?.() && !pm.isLessThanAlarm?.())
            )
      ))
      .sort(labZoneDesc);

    // "Outside Optimal Range Markers" — outside optimal but within lab range, worst first
    const fineTuneGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 3
          : (
              (pm.isGreaterThanOptimal?.() && !pm.isGreaterThanLab?.()) ||
              (!isRdwChart(pm) && pm.isStricklyLessThanOptimal?.() && !pm.isLessThanLab?.())
            )
      ))
      .sort(optimalZoneDesc);

    // Greedy bin-packing: group sections onto pages to minimise blank space.
    // Each unit's height is estimated from marker count; units that fit together
    // share a page (no break between them). The last unit never gets a break-after
    // so that Insights can flow onto the same page if room remains.
    //
    // Tuning guide — increase PAGE_CAPACITY to pack more sections per page;
    // decrease it to be more conservative:
    //
    //   Constant      | Value  | Meaning
    //   --------------|--------|--------------------------------------------------
    //   PAGE_CAPACITY | 900 px | Usable vertical space per page
    //   BASE_HEIGHT   | 180 px | Fixed overhead per section (title + table header)
    //   ROW_HEIGHT    |  35 px | Height per bar-graph marker row
    //   SECTION_GAP   |  24 px | Spacing between sections sharing a page
    const PAGE_CAPACITY = 900;
    const BASE_HEIGHT   = 180;
    const ROW_HEIGHT    = 40;
    const SECTION_GAP   = 24;
    // Notes card overhead (header + padding) + per-line height
    const NOTES_OVERHEAD   = 80;
    const NOTES_LINE_HEIGHT = 22;

    // Returns estimated height of the Section Notes card for a given section,
    // or 0 when withNotes is off or the section has no notes text.
    const sectionNotesHeight = (section: Section): number => {
      if (!this.state.withNotes) return 0;
      const noteObj = bloodworkAnalysis.bloodwork.bloodworkNotes.notes.find(
        n => n.section === section,
      );
      const text = (noteObj?.text ?? '').trimStart();
      if (!text) return 0;
      return NOTES_OVERHEAD + text.split('\n').length * NOTES_LINE_HEIGHT;
    };

    const estH = (markers: PanelMarker[], section?: Section) =>
      BASE_HEIGHT + markers.length * ROW_HEIGHT + (section ? sectionNotesHeight(section) : 0);

    const packUnits = [
      { id: 'IRON',      height: estH(bloodworkAnalysis.getIronPanelMarkers(), Section.IRON),      visible: !isCollapsed(Section.IRON) && bloodworkAnalysis.getIronPanelMarkers().length > 0 },
      { id: 'PLATELETS', height: estH(bloodworkAnalysis.getPlateletPanelMarkers(), Section.PLATELETS), visible: !isCollapsed(Section.PLATELETS) && bloodworkAnalysis.getPlateletPanelMarkers().length > 0 },
      { id: 'GLUCOSE',   height: estH(bloodworkAnalysis.getGlucosePanelMarkers(), Section.GLUCOSE),  visible: !isCollapsed(Section.GLUCOSE) && bloodworkAnalysis.getGlucosePanelMarkers().length > 0 },
      { id: 'KIDNEY',    height: estH(bloodworkAnalysis.getKidneyPanelMarkers(), Section.KIDNEY),   visible: !isCollapsed(Section.KIDNEY) && bloodworkAnalysis.getKidneyPanelMarkers().length > 0 },
      { id: 'PROTEINS',  height: estH(bloodworkAnalysis.getProteinsPanelMarkers(), Section.PROTEINS), visible: !isCollapsed(Section.PROTEINS) && bloodworkAnalysis.getProteinsPanelMarkers().length > 0 },
      // For split sections, count notes on the first part only (notes appear after the first chunk).
      { id: 'NUTRIENTS_1',     height: estH(nutrientsFirstPageMarkers, Section.NUTRIENTS),           visible: !isCollapsed(Section.NUTRIENTS) && nutrientsFirstPageMarkers.length > 0 },
      { id: 'NUTRIENTS_2',     height: estH(nutrientsSecondPageMarkers),                             visible: !isCollapsed(Section.NUTRIENTS) && nutrientsSecondPageMarkers.length > 0 },
      { id: 'ELECTROLYTES',    height: estH(bloodworkAnalysis.getElectrolytePanelMarkers(), Section.ELECTROLYTES),     visible: !isCollapsed(Section.ELECTROLYTES) && bloodworkAnalysis.getElectrolytePanelMarkers().length > 0 },
      { id: 'LIVER',           height: estH(bloodworkAnalysis.getLiverPanelMarkers(), Section.LIVER_BILIARY),           visible: !isCollapsed(Section.LIVER_BILIARY) && bloodworkAnalysis.getLiverPanelMarkers().length > 0 },
      { id: 'LIPIDS',          height: estH(bloodworkAnalysis.getLipidsPanelMarkers(), Section.LIPIDS),          visible: !isCollapsed(Section.LIPIDS) && bloodworkAnalysis.getLipidsPanelMarkers().length > 0 },
      { id: 'INFLAMMATION',    height: estH(bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers(), Section.INFLAMMATION_OXIDATIVE_STRESS), visible: !isCollapsed(Section.INFLAMMATION_OXIDATIVE_STRESS) && bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers().length > 0 },
      { id: 'THYROID',         height: estH(bloodworkAnalysis.getThyroidPanelMarkers(), Section.THYROID),         visible: !isCollapsed(Section.THYROID) && bloodworkAnalysis.getThyroidPanelMarkers().length > 0 },
      { id: 'SEX_HORMONE_1',   height: estH(hormonesFirstPageMarkers, Section.SEX_HORMONE),          visible: !isCollapsed(Section.SEX_HORMONE) && hormonesFirstPageMarkers.length > 0 },
      { id: 'SEX_HORMONE_2',   height: estH(hormonesSecondPageMarkers),                              visible: !isCollapsed(Section.SEX_HORMONE) && hormonesSecondPageMarkers.length > 0 },
      { id: 'PROSTATE',        height: estH(bloodworkAnalysis.getProstatePanelMarkers(), Section.PROSTATE),        visible: !isCollapsed(Section.PROSTATE) && bloodworkAnalysis.getProstatePanelMarkers().length > 0 },
    ];

    const visibleUnits = packUnits.filter(u => u.visible);
    const unitBreakAfter: Record<string, boolean> = {};
    visibleUnits.forEach(u => { unitBreakAfter[u.id] = false; });

    // When WBC is visible it always occupies its own page between IRON and PLATELETS.
    // Exclude IRON from the main pack run so PLATELETS starts fresh and the algorithm
    // correctly packs GLUCOSE (and others) with PLATELETS when there is room.
    const wbcIntervenes = hasWbcValue && !isCollapsed(Section.WBC);
    const unitsToPackUnits = wbcIntervenes
      ? visibleUnits.filter(u => u.id !== 'IRON')
      : visibleUnits;

    let pageUsed = 0;
    for (let i = 0; i < unitsToPackUnits.length; i++) {
      const unit = unitsToPackUnits[i];
      if (i === 0) {
        pageUsed = unit.height;
      } else if (pageUsed + SECTION_GAP + unit.height > PAGE_CAPACITY) {
        unitBreakAfter[unitsToPackUnits[i - 1].id] = true;
        pageUsed = unit.height;
      } else {
        pageUsed += SECTION_GAP + unit.height;
      }
    }
    // If Insights won't fully fit in the remaining space on the last section's page, force it
    // to a fresh page. Estimate conservatively (200px card overhead + 24px/line + 50px buffer)
    // so when we say it fits, it genuinely does and html2pdf won't split the card header off.
    const insightsLineCount = this.state.withNotes && globalNotesText
      ? globalNotesText.split('\n').length
      : 0;
    const insightsEstimatedHeight = insightsLineCount > 0
      ? 200 + insightsLineCount * 24
      : 0;
    const INSIGHTS_SAFETY_BUFFER = 50;
    const insightsNeedsOwnPage =
      insightsEstimatedHeight > 0 &&
      insightsEstimatedHeight > PAGE_CAPACITY - pageUsed - INSIGHTS_SAFETY_BUFFER;

    if (insightsNeedsOwnPage && visibleUnits.length > 0) {
      unitBreakAfter[visibleUnits[visibleUnits.length - 1].id] = true;
    }

    // After placing Insights, how much space is left on that page?
    // When Insights shares the last bar-graph page the base is pageUsed + spacer + Insights.
    // When Insights is on its own fresh page the base is just its own height.
    const DISCLAIMER_ESTIMATED_HEIGHT = 350;
    const pageUsedAfterInsights = insightsEstimatedHeight > 0
      ? (insightsNeedsOwnPage
          ? insightsEstimatedHeight
          : pageUsed + 12 + insightsEstimatedHeight)
      : pageUsed;
    const disclaimerNeedsOwnPage =
      DISCLAIMER_ESTIMATED_HEIGHT > PAGE_CAPACITY - pageUsedAfterInsights;

    // WBC always starts on its own page via an empty break div inserted before it in JSX.
    // Prevent IRON from also breaking-after (which would create a blank page between them).
    if (hasWbcValue && !isCollapsed(Section.WBC)) {
      unitBreakAfter['IRON'] = false;
    }

    const sectionClass = (id: string) => unitBreakAfter[id] ? 'html2pdf__page-break' : '';

    // Build attribute → body-system label map for grouping markers in the dashboard sections.
    const systemGroupDefs = [
      { label: 'Blood Oxygen',        markers: bloodworkAnalysis.getRBCPanelMarkers?.() ?? [] },
      { label: 'Iron Status',         markers: bloodworkAnalysis.getIronPanelMarkers?.() ?? [] },
      { label: 'Immune',              markers: bloodworkAnalysis.getWBCPanelMarkers?.() ?? [] },
      { label: 'Blood Clotting',      markers: bloodworkAnalysis.getPlateletPanelMarkers?.() ?? [] },
      { label: 'Glucose',             markers: bloodworkAnalysis.getGlucosePanelMarkers?.() ?? [] },
      { label: 'Kidney',              markers: bloodworkAnalysis.getKidneyPanelMarkers?.() ?? [] },
      { label: 'Vitamins & Minerals', markers: bloodworkAnalysis.getMineralPanelMarkers?.() ?? [] },
      { label: 'Electrolytes',        markers: bloodworkAnalysis.getElectrolytePanelMarkers?.() ?? [] },
      { label: 'Proteins',            markers: bloodworkAnalysis.getProteinsPanelMarkers?.() ?? [] },
      { label: 'Liver',               markers: bloodworkAnalysis.getLiverPanelMarkers?.() ?? [] },
      { label: 'Lipids',              markers: bloodworkAnalysis.getLipidsPanelMarkers?.() ?? [] },
      { label: 'Inflammation',        markers: bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers?.() ?? [] },
      { label: 'Thyroid',             markers: bloodworkAnalysis.getThyroidPanelMarkers?.() ?? [] },
      { label: 'Hormones',            markers: bloodworkAnalysis.getSexHormonePanelMarkers?.() ?? [] },
      { label: 'Prostate',            markers: bloodworkAnalysis.getProstatePanelMarkers?.() ?? [] },
    ];
    const attrToSystem: Record<string, string> = {};
    systemGroupDefs.forEach(({ label, markers: sysMarkers }) => {
      sysMarkers.forEach(pm => { attrToSystem[pm.attribute] = label; });
    });

    const groupBySystem = (
      markers: PanelMarker[],
      scoreOf: (pm: PanelMarker) => number,
    ) => {
      const groupMap: Record<string, PanelMarker[]> = {};
      const groupBestScore: Record<string, number> = {};
      markers.forEach(pm => {
        const label = attrToSystem[pm.attribute] ?? 'Other';
        const score = scoreOf(pm);
        if (!groupMap[label]) { groupMap[label] = []; groupBestScore[label] = score; }
        else if (score > groupBestScore[label]) { groupBestScore[label] = score; }
        groupMap[label].push(pm);
      });
      return Object.keys(groupMap)
        .sort((a, b) => groupBestScore[b] - groupBestScore[a])
        .map(label => ({ systemLabel: label, markers: groupMap[label] }));
    };

    return (
      <div className="pdf-page" id="test-results-analysis-panel">
        <div className="text-right" style={{ marginTop: 12 }}>
          <Button
            id="pdf-export-button"
            onClick={this.export}
            disabled={!isPdfReady}
            style={{ opacity: isPdfReady ? 1 : 0 }}
          >
            {isPdfReady ? 'Export to PDF' : 'Preparing PDF...'}
          </Button>
        </div>

        {/* Header */}
        <div className="text-center" style={{ marginTop: 24 }}>
          <Image
            id="logo"
            src={this.getLogoUrl()}
            alt="Current Logo"
            fluid
            style={{
              maxHeight: '100px',
              maxWidth: '400px',
              objectFit: 'contain',
            }}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <h3 className="text-center">
            <strong>Blood Work Analysis Report</strong>
          </h3>
          <div style={{ height: 12 }} aria-hidden />
        </div>

        {/* First-page meta */}
        <div style={{ marginTop: 48 }}>
          <div className="html2pdf__page-break">
            <h5>
              <strong>Prepared for</strong>
            </h5>
            <h5>
              {bloodworkAnalysis.bloodwork.client.firstName}{' '}
              {bloodworkAnalysis.bloodwork.client.lastName}
            </h5>
            <div style={{ height: 12 }} aria-hidden />
            {this.props.userProfileUIStore?.currentUserProfile?.preparedBy && (
              <>
                <h5>
                  <strong>Prepared by</strong>
                </h5>
                <h5>
                  {this.props.userProfileUIStore.currentUserProfile.preparedBy}
                </h5>
                <div style={{ height: 12 }} aria-hidden />
              </>
            )}
            <h5>
              <strong>Collection Date</strong>
            </h5>
            <h5>
              <Moment format="MM/DD/YYYY">
                {bloodworkAnalysis.bloodwork.collectionDate}
              </Moment>
            </h5>
            <div style={{ height: 12 }} aria-hidden />
            {this.state.previousBloodwork && (
              <>
                <h5>
                  <strong>Previous Collection Date</strong>
                </h5>
                <h5>
                  <Moment format="MM/DD/YYYY">
                    {this.state.previousBloodwork.collectionDate}
                  </Moment>
                </h5>
                <div style={{ height: 12 }} aria-hidden />
              </>
            )}
            <h5>
              <strong>Analysis Date</strong>
            </h5>
            <h5>
              <Moment format="MM/DD/YYYY">{today}</Moment>
            </h5>
          </div>

          {/* Summary sections — smart 2/3-column layout with dominance stacking */}
          {process.env.REACT_APP_ENV !== 'prod' && ((!isCollapsed(Section.NEEDS_ATTENTION) && needsAttentionGroup.length > 0) ||
            (!isCollapsed(Section.WORTH_DISCUSSING) && worthDiscussingGroup.length > 0) ||
            (!isCollapsed(Section.FINE_TUNE) && fineTuneGroup.length > 0)) && (
            <div className="html2pdf__page-break">
              {(() => {
                const alarmN = !isCollapsed(Section.NEEDS_ATTENTION)  ? needsAttentionGroup.length  : 0;
                const labN   = !isCollapsed(Section.WORTH_DISCUSSING) ? worthDiscussingGroup.length : 0;
                const optN   = !isCollapsed(Section.FINE_TUNE)        ? fineTuneGroup.length        : 0;
                const optDominant   = optN   > alarmN + labN && alarmN > 0 && labN > 0;
                const alarmDominant = alarmN >= labN + optN  && labN   > 0 && optN > 0;
                const GAP = '8px';
                const alarmCard = alarmN > 0 ? (
                  <div style={{ flex: 1, minWidth: 0, maxWidth: 600 }}>
                    <SummaryDashboardSection
                      title="Alarm Range Markers"
                      section={Section.NEEDS_ATTENTION}
                      groups={groupBySystem(needsAttentionGroup, pm => zonePenetrationScore(pm, 'alarm'))}
                    />
                  </div>
                ) : null;
                const labCard = labN > 0 ? (
                  <div style={{ flex: 1, minWidth: 0, maxWidth: 600 }}>
                    <SummaryDashboardSection
                      title="Outside Lab Range Markers"
                      section={Section.WORTH_DISCUSSING}
                      groups={groupBySystem(worthDiscussingGroup, pm => zonePenetrationScore(pm, 'lab'))}
                    />
                  </div>
                ) : null;
                const optCard = optN > 0 ? (
                  <div style={{ flex: 1, minWidth: 0, maxWidth: 600 }}>
                    <SummaryDashboardSection
                      title="Outside Optimal Range Markers"
                      section={Section.FINE_TUNE}
                      groups={groupBySystem(fineTuneGroup, pm => zonePenetrationScore(pm, 'optimal'))}
                    />
                  </div>
                ) : null;
                if (optDominant) {
                  return (
                    <div style={{ display: 'flex', gap: GAP, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: GAP }}>
                        {alarmCard}{labCard}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>{optCard}</div>
                    </div>
                  );
                }
                if (alarmDominant) {
                  return (
                    <div style={{ display: 'flex', gap: GAP, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>{alarmCard}</div>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: GAP }}>
                        {labCard}{optCard}
                      </div>
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'flex', gap: GAP, alignItems: 'flex-start' }}>
                    {alarmCard}
                    {labCard}
                    {optCard}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Per-system sections */}
          {!isCollapsed(Section.RBC) && (
            <div>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Blood Oxygen & Red Blood Cell Function"
                section={Section.RBC}
                panelMarkers={bloodworkAnalysis.getRBCPanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.IRON) && (
            <div className={sectionClass('IRON')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Iron Status & Utilization"
                section={Section.IRON}
                panelMarkers={bloodworkAnalysis.getIronPanelMarkers()}
              />
            </div>
          )}

          {hasWbcValue && !isCollapsed(Section.WBC) && (
            <>
              {/* Only break if RBC or IRON rendered before WBC; otherwise WBC is already first */}
              {(!isCollapsed(Section.RBC) || (!isCollapsed(Section.IRON) && bloodworkAnalysis.getIronPanelMarkers().length > 0)) && (
                <div className="html2pdf__page-break" />
              )}
              <div>
                <PDFSection
                  withNotes={this.state.withNotes}
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Immune Activity"
                  section={Section.WBC}
                  panelMarkers={bloodworkAnalysis.getWBCPanelMarkers()}
                />
              </div>
            </>
          )}

          {!isCollapsed(Section.PLATELETS) && (
            <div className={sectionClass('PLATELETS')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Blood Clotting Function"
                section={Section.PLATELETS}
                panelMarkers={bloodworkAnalysis.getPlateletPanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.GLUCOSE) && (
            <div className={sectionClass('GLUCOSE')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Glucose Regulation & Pancreatic Function"
                section={Section.GLUCOSE}
                panelMarkers={bloodworkAnalysis.getGlucosePanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.KIDNEY) && (
            <div className={sectionClass('KIDNEY')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Kidney Function"
                section={Section.KIDNEY}
                panelMarkers={bloodworkAnalysis.getKidneyPanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.PROTEINS) && (
            <div className={sectionClass('PROTEINS')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Protein Status"
                section={Section.PROTEINS}
                panelMarkers={bloodworkAnalysis.getProteinsPanelMarkers()}
              />
            </div>
          )}

          {/* Vitamin & Mineral Status (with automatic split after 13 markers) */}
          {mineralMarkers.length > 0 && !isCollapsed(Section.NUTRIENTS) && (
            <>
              <div className={sectionClass('NUTRIENTS_1')}>
                <PDFSection
                  withNotes={this.state.withNotes}
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Vitamin & Mineral Status"
                  section={Section.NUTRIENTS}
                  panelMarkers={nutrientsFirstPageMarkers}
                />
              </div>

              {nutrientsSecondPageMarkers.length > 0 && (
                <div className={sectionClass('NUTRIENTS_2')}>
                  <PDFSection
                    withNotes={this.state.withNotes}
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title="Vitamin & Mineral Status (cont.)"
                    section={Section.NUTRIENTS}
                    panelMarkers={nutrientsSecondPageMarkers}
                  />
                </div>
              )}
            </>
          )}

          {!isCollapsed(Section.ELECTROLYTES) && (
            <div className={sectionClass('ELECTROLYTES')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Electrolyte Balance"
                section={Section.ELECTROLYTES}
                panelMarkers={bloodworkAnalysis.getElectrolytePanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.LIVER_BILIARY) && (
            <div className={sectionClass('LIVER')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Liver Function"
                section={Section.LIVER_BILIARY}
                panelMarkers={bloodworkAnalysis.getLiverPanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.LIPIDS) && (
            <div className={sectionClass('LIPIDS')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Lipid Balance"
                section={Section.LIPIDS}
                panelMarkers={bloodworkAnalysis.getLipidsPanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.INFLAMMATION_OXIDATIVE_STRESS) && (
            <div className={sectionClass('INFLAMMATION')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Inflammation & Oxidative Stress"
                section={Section.INFLAMMATION_OXIDATIVE_STRESS}
                panelMarkers={bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers()}
              />
            </div>
          )}

          {!isCollapsed(Section.THYROID) && (
            <div className={sectionClass('THYROID')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Thyroid Function"
                section={Section.THYROID}
                panelMarkers={bloodworkAnalysis.getThyroidPanelMarkers()}
              />
            </div>
          )}

          {/* Hormones Analysis (with automatic split after 13 markers) */}
          {sexHormoneMarkers.length > 0 && !isCollapsed(Section.SEX_HORMONE) && (
            <>
              <div className={sectionClass('SEX_HORMONE_1')}>
                <PDFSection
                  withNotes={this.state.withNotes}
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Hormone Balance"
                  section={Section.SEX_HORMONE}
                  panelMarkers={hormonesFirstPageMarkers}
                />
              </div>

              {hormonesSecondPageMarkers.length > 0 && (
                <div className={sectionClass('SEX_HORMONE_2')}>
                  <PDFSection
                    withNotes={this.state.withNotes}
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title="Hormone Balance (cont.)"
                    section={Section.SEX_HORMONE}
                    panelMarkers={hormonesSecondPageMarkers}
                  />
                </div>
              )}
            </>
          )}

          {!isCollapsed(Section.PROSTATE) && (
            <div className={sectionClass('PROSTATE')}>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Prostate Function"
                section={Section.PROSTATE}
                panelMarkers={bloodworkAnalysis.getProstatePanelMarkers()}
              />
            </div>
          )}

          {/* Global Notes — flows onto last bar-graph page when room remains;
              the break is forced on the last section above when it won't fit. */}
          {this.state.withNotes && globalNotesText && (
            <>
              {!insightsNeedsOwnPage && <div style={{ height: 12 }} />}
              <Card
                className="disclaimer-panel mb-5"
                style={{ whiteSpace: 'pre-line' }}
              >
                <Card.Header className="py-1">Insights</Card.Header>
                <Card.Body
                  className="disclaimer-body"
                  style={{ whiteSpace: 'pre-line', paddingTop: 0 }}
                >
                  {globalNotesText}
                </Card.Body>
              </Card>
            </>
          )}

          {/* Disclaimer — forced to its own page when it won't fit after Insights */}
          {disclaimerNeedsOwnPage && <><div className="html2pdf__page-break" /><div style={{ height: 12 }} /></>}
          <DisclaimerSection furtherConsiderations={furtherConsiderations} />
        </div>
      </div>
    );
  }
}
